import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import clsx from "clsx";
import { db, eq, ilike } from "../lib/api";
import { invalidate, useDebounced, useQuery } from "../lib/hooks";
import { navigate } from "../lib/router";
import { AREA_LABEL, COUNT_LABEL, useRefData, type Area } from "../lib/refdata";
import { useSession } from "../lib/session";
import { PageHead } from "../components/Shell";
import { ErrorBox, Field, Loading, Panel, money, useToast } from "../components/ui";
import { openPerson } from "../components/PersonDrawer";
import { GxPanel } from "../components/Gx";

const COUNTS = ["outreach", "follow_ups", "appointments", "client_moves", "recruit_moves", "builder_moves"] as const;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
type Plan = { week_start: string; targets: Record<string, any>; focus_people: string[]; focus_clients: string[]; stretch: string | null;
  went_well: string | null; do_differently: string | null; grade: any; locked_at: string | null };
type Cycle = { start: string; end: string; weeks: number; week: number; days_left: number;
  goals: { key: string; label: string; goal: number; actual: number; placed?: number | null; money?: boolean | null; expected_now: number;
    needed_per_week: number | null; forecast?: number | null; status: string }[];
  coach?: { conversations_per_day: number | null; appointments_per_week: number | null; presentations_per_week: number | null; events_per_week: number | null;
    book_rate: number; close_rate: number; join_rate: number; estimated: boolean } };

/** Which week we're grading and which we're planning: Fri–Sun plan next week; Mon–Thu plan this week. */
function weeks() {
  const monday = dayjs().startOf("week").add(1, "day"); // dayjs weeks start Sunday
  const thisMon = dayjs().day() === 0 ? monday.subtract(7, "day") : monday;
  const lateWeek = dayjs().day() >= 5 || dayjs().day() === 0;
  return {
    grade: (lateWeek ? thisMon : thisMon.subtract(7, "day")).format("YYYY-MM-DD"),
    plan: (lateWeek ? thisMon.add(7, "day") : thisMon).format("YYYY-MM-DD"),
  };
}

/** The 90-day plan: results against your goals (submitted and placed), pace, forecast, and what it takes from here. */
export function CycleBars({ compact, coach }: { compact?: boolean; coach?: boolean }) {
  const q = useQuery("cycle:progress", () => db.rpc<Cycle>("cycle_progress"), { staleMs: 300_000 });
  const c = q.data;
  if (!c?.goals?.length) return null;
  const fmt = (g: Cycle["goals"][number], v: number | null | undefined) => v == null ? "0" : g.money ? money(Number(v)) : Number(v).toLocaleString("en-CA");
  const k = c.coach;
  const todo = k && [k.conversations_per_day && `${k.conversations_per_day} real conversation${k.conversations_per_day === 1 ? "" : "s"} a day`,
    k.appointments_per_week && `${k.appointments_per_week} appointment${k.appointments_per_week === 1 ? "" : "s"} a week`,
    k.presentations_per_week && `${k.presentations_per_week} business presentation${k.presentations_per_week === 1 ? "" : "s"} a week`,
    k.events_per_week && `${k.events_per_week} event${k.events_per_week === 1 ? "" : "s"} a week`].filter(Boolean);
  return (
    <Panel title={`Results · 90-day plan · week ${c.week} of ${c.weeks}`} hint={`${c.days_left} days left · ends ${dayjs(c.end).format("MMM D")}`}
      actions={coach ? <button className="linkish small" onClick={() => navigate("/settings/goals")}>Set goals</button> : undefined}>
      <div className="panel-body cycle">
        {c.goals.map((g) => {
          const pct = g.goal ? Math.min(100, (g.actual / g.goal) * 100) : 0;
          const ppct = g.goal && g.placed != null ? Math.min(100, (Number(g.placed) / g.goal) * 100) : null;
          const exp = g.goal ? Math.min(100, (g.expected_now / g.goal) * 100) : 0;
          return (
            <div key={g.key} className="cyc">
              <div className="cyc-h"><strong>{g.label}</strong><span className={clsx("pill", g.status === "behind" ? "alert" : "warm")}>{g.status === "done" ? "Goal hit" : g.status === "ahead" ? "On pace" : "Behind pace"}</span></div>
              <div className="cyc-bar" aria-label={`${g.actual} of ${g.goal}`}><i style={{ width: `${pct}%` }} className={g.status === "behind" ? "behind" : ""} />
                {ppct != null && <i className="placed" style={{ width: `${ppct}%` }} />}<b style={{ left: `${exp}%` }} title="Where you should be by now" /></div>
              <div className="small"><b>{fmt(g, g.actual)}</b> of {fmt(g, g.goal)}{g.placed != null ? <> submitted · <b>{fmt(g, g.placed)}</b> placed</> : ""}</div>
              <div className="small muted">should be at {fmt(g, g.expected_now)} by now{g.forecast != null && g.status !== "done" ? ` · on this pace: ${fmt(g, g.forecast)}` : ""}{!compact && g.needed_per_week != null && g.status !== "done" ? ` · need ${fmt(g, g.needed_per_week)}/week` : ""}</div>
            </div>
          );
        })}
      </div>
      {coach && todo && todo.length > 0 && (
        <div className="panel-body cyc-coach small">
          <b>To hit your goals from here:</b> {todo.join(" · ")}.
          <span className="muted"> {k!.estimated ? "Using starter rates (1 in 3 books, 1 in 3 closes) until you have more history." : `Your rates: ${Math.round(k!.book_rate * 100)}% of conversations book, ${Math.round(k!.close_rate * 100)}% of appointments close.`}</span>
        </div>)}
    </Panel>
  );
}

function PersonPick({ label, ids, onChange, max, clientsOnly }: { label: string; ids: string[]; onChange: (v: string[]) => void; max: number; clientsOnly?: boolean }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const found = useQuery(dq.length > 1 ? `wr-find:${clientsOnly}:${dq}` : null, () => db.select<{ id: string; full_name: string }>("people",
    { select: "id,full_name", full_name: ilike(dq), deleted_at: "is.null", ...(clientsOnly ? { is_client: "is.true" } : {}), limit: 8 }));
  const names = useQuery(ids.length ? `wr-names:${ids.join(",")}` : null, () => db.select<{ id: string; full_name: string }>("people", { select: "id,full_name", id: `in.(${ids.join(",")})` }));
  const name = (id: string) => names.data?.find((p) => p.id === id)?.full_name ?? "…";
  return (
    <Field label={`${label} (${ids.length}/${max})`} wide>
      <div className="chips">{ids.map((id) => (
        <span key={id} className="chip"><button className="linkish" onClick={() => openPerson(id)}>{name(id)}</button>
          <button className="chip-x" aria-label="Remove" onClick={() => onChange(ids.filter((x) => x !== id))}>×</button></span>))}</div>
      {ids.length < max && <>
        <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={clientsOnly ? "Search a client" : "Search a person"} />
        {(found.data ?? []).filter((p) => !ids.includes(p.id)).map((p) => (
          <button key={p.id} className="btn ghost small" style={{ justifyContent: "flex-start" }} onClick={() => { onChange([...ids, p.id]); setQ(""); }}>{p.full_name}</button>))}
      </>}
    </Field>
  );
}

export default function WeeklyReview() {
  const ref = useRefData();
  const toast = useToast();
  const { session } = useSession();
  const me = session?.user.id;
  const w = useMemo(weeks, []);
  const grade = useQuery(`wr:grade:${w.grade}`, () => db.rpc<any>("week_grade", { p_week: w.grade }));
  const plan = useQuery(me ? `wr:plan:${w.plan}` : null, () => db.one<Plan>("weekly_plans", { user_id: eq(me!), week_start: eq(w.plan), select: "*" }));
  const cyc = useQuery("cycle:progress", () => db.rpc<Cycle>("cycle_progress"), { staleMs: 300_000 });
  const reviewList = useQuery("cc:items:all:review", () => db.rpc<any[]>("command_center", { p_scope: "all" }));
  const defaults = ref.data?.setting("daily_targets") ?? {};
  const onDefault = ref.data?.setting("targets_on_days") ?? [true, true, true, true, false, false, false];
  const [f, setF] = useState<Plan | null>(null);
  const [err, setErr] = useState<Error | null>(null);
  useEffect(() => {
    if (f || !ref.data || plan.loading) return;
    const p = plan.data;
    const targets: Record<string, any> = { on: p?.targets?.on ?? onDefault };
    for (const k of COUNTS) targets[k] = p?.targets?.[k] ?? defaults[k] ?? [0, 0, 0, 0, 0, 0, 0];
    setF({ week_start: w.plan, targets, focus_people: p?.focus_people ?? [], focus_clients: p?.focus_clients ?? [], stretch: p?.stretch ?? "",
      went_well: p?.went_well ?? "", do_differently: p?.do_differently ?? "", grade: p?.grade ?? null, locked_at: p?.locked_at ?? null });
  }, [ref.data, plan.data, plan.loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const g = grade.data;
  const locked = !!f?.locked_at;
  const setT = (k: string, i: number, v: string) => setF((x) => x && ({ ...x, targets: { ...x.targets, [k]: x.targets[k].map((n: number, j: number) => (j === i ? Math.max(0, Number(v) || 0) : n)) } }));
  const setOn = (i: number) => setF((x) => x && ({ ...x, targets: { ...x.targets, on: x.targets.on.map((b: boolean, j: number) => (j === i ? !b : b)) } }));
  const weekTotal = (k: string) => (f?.targets[k] ?? []).reduce((s: number, n: number, i: number) => s + (f?.targets.on[i] ? n : 0), 0);
  // Suggested appointments from the 90-day goal: ~3 appointments per new client
  const clientsGoal = cyc.data?.goals?.find((x) => x.key === "clients");
  const suggestAppts = clientsGoal?.needed_per_week != null ? Math.ceil(clientsGoal.needed_per_week * 3) : null;

  async function save(lock: boolean) {
    if (!f) return;
    setErr(null);
    try {
      if (lock && f.focus_people.length === 0) throw new Error("Choose at least one person you'll move forward this week.");
      const body = { user_id: me, week_start: f.week_start, targets: f.targets, focus_people: f.focus_people, focus_clients: f.focus_clients,
        stretch: f.stretch || null, went_well: f.went_well || null, do_differently: f.do_differently || null,
        ...(lock ? { grade: g, locked_at: new Date().toISOString() } : {}) };
      await db.upsert("weekly_plans", body, "user_id,week_start");
      invalidate("wr:"); invalidate("cc"); invalidate("today"); invalidate("scoreboard");
      if (lock) setF({ ...f, locked_at: new Date().toISOString() });
      toast(lock ? "Week locked: these are your targets now" : "Draft saved");
    } catch (e) { setErr(e as Error); }
  }

  const review = (reviewList.data ?? []);
  const rv = (kind: string, test?: (i: any) => boolean) => review.filter((i) => i.kind === kind && (!test || test(i)));

  return (
    <>
      <PageHead help="review" title="Weekly review" sub={`Grade the week of ${dayjs(w.grade).format("MMM D")}, then commit to the week of ${dayjs(w.plan).format("MMM D")}. About 30 minutes. Locking it makes your numbers the targets for that week.`} />
      <ErrorBox error={err || grade.error || plan.error} />
      <div style={{ display: "grid", gap: 14, minWidth: 0 }}>
          <GxPanel />
          <Panel title={`1 · Grade: week of ${dayjs(w.grade).format("MMM D")}`} hint={g ? `${g.days_on_target} of ${g.days_on} days on target · average ${g.avg_score != null ? Math.round(g.avg_score * 100) + "%" : "—"}` : undefined}>
            {!g ? <Loading rows={4} /> : (
              <div className="table-wrap">
                <table className="data">
                  <thead><tr><th>Count</th><th className="num">Actual</th><th className="num">Target</th><th>Result</th></tr></thead>
                  <tbody>
                    {COUNTS.map((k) => {
                      const c = g.counts?.[k] ?? { actual: 0, target: 0 };
                      const ok = c.target === 0 || c.actual >= c.target;
                      return <tr key={k}><td>{COUNT_LABEL[k]}</td><td className="num">{c.actual}</td><td className="num">{c.target}</td>
                        <td>{c.target ? <span className={clsx("pill", ok ? "warm" : "alert")}>{ok ? "Hit" : `Short ${c.target - c.actual}`}</span> : <span className="muted small">No target</span>}</td></tr>;
                    })}
                    <tr><td>Days closed</td><td className="num">{g.days_closed}</td><td className="num">{g.days_on}</td><td /></tr>
                    <tr><td>Promises kept</td><td className="num">{g.promises_kept}</td><td className="num">{g.promises_kept + g.promises_broken + g.promises_open}</td>
                      <td>{g.promises_broken ? <span className="pill alert">{g.promises_broken} broken</span> : null}{g.promises_open ? <span className="pill cooling">{g.promises_open} open</span> : null}</td></tr>
                    <tr><td>Follow-ups done on time</td><td className="num">{g.followups_done}</td><td className="num">{g.followups_done + g.followups_missed}</td><td>{g.followups_missed ? <span className="pill alert">{g.followups_missed} missed</span> : null}</td></tr>
                    <tr><td>Tasks done</td><td className="num">{g.tasks_done}</td><td className="num">{g.tasks_due}</td><td /></tr>
                  </tbody>
                </table>
              </div>
            )}
            {f && <div className="panel-body form-grid">
              <Field label="What went well"><textarea className="textarea" rows={2} disabled={locked} value={f.went_well ?? ""} onChange={(e) => setF({ ...f, went_well: e.target.value })} /></Field>
              <Field label="What I'll do differently"><textarea className="textarea" rows={2} disabled={locked} value={f.do_differently ?? ""} onChange={(e) => setF({ ...f, do_differently: e.target.value })} /></Field>
            </div>}
          </Panel>

          <WeekResults week={w.grade} />

          <Panel title="3 · Review" hint="Clear or decide on each before you plan">
            {reviewList.loading && !reviewList.data ? <Loading rows={3} /> : (
              <ul className="cc-list">
                {[
                  ["Pipeline cards with no next action", rv("pipeline", (i) => /no next action/.test(i.why)).length, "/leads/pipeline"],
                  ["Stale pipeline cards", rv("pipeline", (i) => /no activity/.test(i.why)).length, "/leads/pipeline"],
                  ["Booked but not in Meetings", review.filter((i) => i.item_key.startsWith("book:")).length, "/leads/pipeline"],
                  ["9-month marks within 30 days", rv("licensing").length, "/team/dashboard"],
                  ["Late promised follow-ups", rv("follow_up", (i) => (i.days_late ?? 0) > 0).length, "/command?scope=all"],
                  ["Commitments past due", rv("commitment").length, "/command?scope=all"],
                ].map(([label, n, to]) => (
                  <li key={label as string} className={clsx("cc-item", (n as number) ? "lv-amber" : "")}>
                    <button className="cc-main" onClick={() => navigate(to as string)}><span className="cc-title">{label}</span></button>
                    <div className="cc-side"><span className={clsx("pill", (n as number) ? "cooling" : "warm")}>{n as number}</span></div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title={`4 · Commit: week of ${dayjs(w.plan).format("MMM D")}`} hint={locked ? `Locked ${dayjs(f!.locked_at).format("ddd MMM D, h:mm A")}` : "Set your numbers per day; untick days off"}>
            {!f ? <Loading rows={4} /> : <>
              <div className="table-wrap">
                <table className="data plan-grid">
                  <thead><tr><th />{DAYS.map((d, i) => <th key={d} className="num"><label className="switch-row" style={{ margin: 0, justifyContent: "flex-end" }}>
                    <input type="checkbox" disabled={locked} checked={!!f.targets.on[i]} onChange={() => setOn(i)} />{d}</label></th>)}<th className="num">Week</th></tr></thead>
                  <tbody>
                    {COUNTS.map((k) => (
                      <tr key={k}><td>{COUNT_LABEL[k]}{k === "appointments" && suggestAppts != null && <div className="small muted">Your 90-day goal needs about {suggestAppts}/week</div>}</td>
                        {DAYS.map((_, i) => <td key={i} className="num"><input className="input num-in" type="number" min={0} disabled={locked || !f.targets.on[i]}
                          value={f.targets[k][i]} onChange={(e) => setT(k, i, e.target.value)} /></td>)}
                        <td className="num"><b>{weekTotal(k)}</b></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="panel-body form-grid">
                <PersonPick label="The 5 people you'll move forward" ids={f.focus_people} max={5} onChange={(v) => !locked && setF({ ...f, focus_people: v })} />
                <PersonPick label="The 3 clients you'll review" ids={f.focus_clients} max={3} clientsOnly onChange={(v) => !locked && setF({ ...f, focus_clients: v })} />
                <Field label="One stretch goal" wide><input className="input" disabled={locked} value={f.stretch ?? ""} onChange={(e) => setF({ ...f, stretch: e.target.value })} placeholder="e.g. Book 2 Business Owner Strategy Sessions" /></Field>
              </div>
              {!locked && <div className="panel-body row-inline" style={{ justifyContent: "flex-end" }}>
                <button className="btn" onClick={() => save(false)}>Save draft</button>
                <button className="btn primary" onClick={() => save(true)}>Lock the week</button>
              </div>}
            </>}
          </Panel>
      </div>
    </>
  );
}

type Res = { new_contacts: number; new_by_list: Record<string, number>; conversations: number; appointments_held: number; people_touched: number;
  deals_opened: number; deals_moved: number; deals_won: number; premium: number; new_clients: number; recruits: number; events: number };
type GoalWeek = { id: string; title: string; period: "day" | "week"; target: number; actual: number; goal_total: number; days_hit: number | null; days_on: number | null; hit: boolean };

/** What the week produced, next to the week before, and how the goals went. */
function WeekResults({ week }: { week: string }) {
  const q = useQuery(`wr:results:${week}`, () => db.rpc<{ scope: string; this: Res; prev: Res; goals: GoalWeek[] }>("week_results", { p_week: week }));
  const d = q.data;
  const tile = (label: string, k: keyof Res, fmt: (n: number) => string = (n) => String(n), sub?: string) => {
    const a = Number(d!.this[k]) || 0, b = Number(d!.prev[k]) || 0;
    return (
      <div className="result" key={k}>
        <b>{fmt(a)}</b><span className="lbl">{label}</span>
        {sub && <span className="small muted">{sub}</span>}
        <span className={clsx("delta", a > b ? "up" : a < b ? "down" : "")}>{a === b ? "same as last week" : `${a > b ? "▲" : "▼"} ${fmt(Math.abs(a - b))} vs last week`}</span>
      </div>
    );
  };
  const byList = d ? Object.entries(d.this.new_by_list ?? {}).map(([k, n]) => `${AREA_LABEL[k as Area] ?? k} ${n}`).join(" · ") : "";
  return (
    <Panel title={`2 · Results: week of ${dayjs(week).format("MMM D")}`} hint={d?.scope === "business" ? "The whole business" : "Your own"}>
      {!d ? <Loading rows={3} /> : <div className="panel-body" style={{ display: "grid", gap: 14 }}>
        <div className="results">
          {tile("New contacts", "new_contacts", undefined, byList || undefined)}
          {tile("Two-way conversations", "conversations")}
          {tile("Appointments held", "appointments_held")}
          {tile("Deals opened", "deals_opened")}
          {tile("Deals moved forward", "deals_moved")}
          {tile("Deals won", "deals_won")}
          {tile("Premium won (monthly)", "premium", (n) => money(n))}
          {tile("New clients", "new_clients")}
          {tile("Recruits", "recruits")}
          {tile("Events attended", "events")}
        </div>
        {d.goals.length > 0 && <div className="table-wrap"><table className="data">
          <thead><tr><th>Goal</th><th className="num">Done</th><th className="num">Goal</th><th>Result</th></tr></thead>
          <tbody>{d.goals.map((x) => (
            <tr key={x.id}><td>{x.title}<div className="small muted">{x.target} a {x.period}</div></td>
              <td className="num">{x.actual}</td><td className="num">{x.goal_total}</td>
              <td>{x.period === "day" ? <span className={clsx("pill", x.hit ? "warm" : "alert")}>{x.days_hit} of {x.days_on} days</span>
                : <span className={clsx("pill", x.hit ? "warm" : "alert")}>{x.hit ? "Hit" : `Short ${x.target - x.actual}`}</span>}</td></tr>
          ))}</tbody>
        </table></div>}
      </div>}
    </Panel>
  );
}

/** Command Center: this week's commitments from the locked plan. */
export function ThisWeek({ bare }: { bare?: boolean } = {}) {
  const { session } = useSession();
  const me = session?.user.id;
  const wk = dayjs().day() === 0 ? dayjs().subtract(6, "day").format("YYYY-MM-DD") : dayjs().startOf("week").add(1, "day").format("YYYY-MM-DD");
  const plan = useQuery(me ? `wr:plan:${wk}` : null, () => db.one<Plan>("weekly_plans", { user_id: eq(me!), week_start: eq(wk), select: "*" }));
  const ids = [...(plan.data?.focus_people ?? []), ...(plan.data?.focus_clients ?? [])];
  const people = useQuery(ids.length ? `wr-names:${ids.join(",")}:touch` : null, () =>
    db.select<{ id: string; full_name: string }>("people", { select: "id,full_name", id: `in.(${ids.join(",")})` }));
  const touched = useQuery(ids.length ? `wr-touched:${wk}:${ids.join(",")}` : null, () =>
    db.select<{ person_id: string }>("activities", { select: "person_id", person_id: `in.(${ids.join(",")})`, occurred_on: `gte.${wk}` }));
  const p = plan.data;
  if (!p?.locked_at) return null;
  const done = new Set((touched.data ?? []).map((t) => t.person_id));
  const nm = (id: string) => people.data?.find((x) => x.id === id)?.full_name ?? "…";
  const row = (id: string, kind: string) => (
    <li key={id} className={clsx("cc-item", done.has(id) ? "" : "lv-amber")}>
      <button className="cc-main" onClick={() => openPerson(id)}><span className="cc-area">{kind}</span><span className="cc-title">{nm(id)}</span></button>
      <div className="cc-side">{done.has(id) ? <span className="pill warm">Moved this week</span> : <span className="pill cooling">Not yet</span>}</div>
    </li>
  );
  if (bare) return (
    <div className="cc-week">
      <div className="panel-body" style={{ paddingBottom: 4 }}><b className="small">This week's commitments</b>{p.stretch && <span className="small muted"> · stretch: {p.stretch}</span>}
        <button className="linkish small" style={{ float: "right" }} onClick={() => navigate("/review")}>Plan</button></div>
      <ul className="cc-list">{p.focus_people.map((id) => row(id, "Move forward"))}{p.focus_clients.map((id) => row(id, "Review"))}</ul>
    </div>
  );
  return (
    <Panel title="This week's commitments" hint={p.stretch ? `Stretch: ${p.stretch}` : "From your weekly review"}
      actions={<button className="btn small ghost" onClick={() => navigate("/review")}>Plan</button>}>
      <ul className="cc-list">{p.focus_people.map((id) => row(id, "Move forward"))}{p.focus_clients.map((id) => row(id, "Review"))}</ul>
    </Panel>
  );
}
